import React, { useEffect, useState, useRef } from "react";
import {
    Modal,
    ScrollView,
    Text,
    TouchableOpacity,
    View,
    Image,
    StyleSheet,
} from "react-native";
import { LinearGradient } from "expo-linear-gradient";
import { ProgressBar } from "react-native-paper";
import { AppColors } from "../../../../utility/AppColors";
import { AppImages } from "../../../../utility/AppImages";
import { Base64 } from "js-base64";
import ImageComponent from "../../../../Components/ImageComponent";
import { format, isValid } from "date-fns";
import { getEventForNotLoginUser } from "../../../../services/eventsAPI";
import { toast } from "react-toastify";
import Avatar from "../../../../Components/choose-avatar-components/Avatar";
import { Badge } from "../../../../Components/notification-common-components/Badge";
import {
    parseEventShareURL,
    storeEventShareAction,
} from "../../../../utility/urlUtils";
import { useUserData } from "../../../../context/UserDataContext";
import { useNavigation } from "@react-navigation/native";
import { navigateToScreen } from "../../../../utility/NavigationHelper";
import { AppScreens } from "../../../Utility/AppScreens";
import { base_url, endpoints } from "../../../../Api/configs";
import { storeUserActivity, ACTIVITY_TYPES } from "../../../../services/activityService";

interface MobileEventSharePopupProps {
    showModal: boolean;
    setShowModal: (show: boolean) => void;
    onActionPress?: (action: string) => void;
    eventShareURL?: string | null;
    eventId?: string | null;
}

export function MobileEventSharePopup(props: MobileEventSharePopupProps) {
    const {
        showModal = false,
        setShowModal,
        onActionPress,
        eventShareURL = null,
        eventId = null,
    } = props;

    const navigation = useNavigation();
    const { userData } = useUserData();
    const [showCreateLoader, setCreateShowLoader] = useState(false);
    const [eventDetails, setEventDetails] = useState<any>(null);
    const [showDetails, setShowDetails] = useState(false);
    const [isNegotiating, setIsNegotiating] = useState(false);
    const scrollViewRef = useRef<ScrollView>(null);

    useEffect(() => {
        if (showModal) {
            fetchEventDetailsFromURL();
        }
    }, [showModal]);

    const fetchEventDetailsFromURL = () => {
        try {
            let extractedEventId = eventId;

            if (!extractedEventId && eventShareURL) {
                const urlData = parseEventShareURL(eventShareURL);
                extractedEventId = urlData.eventId;
            }

            if (!extractedEventId) {
                const urlParams = new URLSearchParams(window.location.search);
                const sessionParam = urlParams.get("session");
                if (sessionParam) {
                    const decodedSession = Base64.decode(sessionParam);
                    const sessionData = JSON.parse(decodedSession);
                    extractedEventId = sessionData.eventId;
                }
            }

            if (extractedEventId) {
                callGetEventDetails(extractedEventId);
            } else {
                toast.error("Event ID not found");
                setShowModal(false);
            }
        } catch (error) {
            toast.error("Invalid event link");
            setShowModal(false);
        }
    };

    const callGetEventDetails = async (eventId: string) => {
        setCreateShowLoader(true);
        try {
            const response = await getEventForNotLoginUser(eventId);
            if (response.event) {
                setEventDetails(response.event);
            } else {
                toast.error("Failed to fetch event details");
                setShowModal(false);
            }
        } catch (error) {
            toast.error("Failed to fetch event details");
            setShowModal(false);
        } finally {
            setCreateShowLoader(false);
        }
    };

    const handleActionPress = async (action: string) => {
        if (action === "negotiate") {
            await handleNegotiateTerms();
            return;
        }

        storeEventShareAction(action);

        const signupFlowContextStr = sessionStorage.getItem("signupFlowContext");
        if (signupFlowContextStr) {
            try {
                const signupFlowContext = JSON.parse(signupFlowContextStr);
                signupFlowContext.notificationAction = action;
                sessionStorage.setItem("signupFlowContext", JSON.stringify(signupFlowContext));
            } catch (error) {
                console.warn("Error updating signupFlowContext:", error);
            }
        }

        if (onActionPress) {
            onActionPress(action);
        }
        setShowModal(false);
    };

    const handleNegotiateTerms = async () => {
        if (!userData || !userData.AccessToken) {
            toast.info("Please log in to negotiate terms");
            storeEventShareAction("negotiate");
            setShowModal(false);
            navigateToScreen(navigation, AppScreens.LoginScreen);
            return;
        }

        setIsNegotiating(true);
        try {
            const response = await fetch(
                `${base_url}${endpoints.events.negotiateTerms}`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${userData.AccessToken}`,
                    },
                    body: JSON.stringify({
                        accessToken: userData.AccessToken,
                        eventId: eventDetails?.eventID,
                        newOpponentId: userData.username || userData.userID,
                    }),
                }
            );

            const result = await response.json();

            if (response.ok && (result.message || result.chatId)) {
                toast.success("Terms negotiated successfully");
                storeUserActivity(
                    userData.AccessToken,
                    eventDetails?.eventID,
                    ACTIVITY_TYPES.NEGOTIATION_STARTED
                ).catch((err) => console.warn("Failed to track activity:", err));

                setShowModal(false);
                navigateToScreen(navigation, AppScreens.OnGoingEvents, {
                    eventID: eventDetails?.eventID,
                    tab: "Chat",
                });
            } else {
                toast.error(result.error || "Failed to negotiate terms");
            }
        } catch (error) {
            toast.error("Failed to negotiate terms. Please try again.");
        } finally {
            setIsNegotiating(false);
        }
    };

    const formatDate = (date: string) => {
        const eventDate = new Date(date);
        if (!isValid(eventDate)) return "Invalid date";
        return format(eventDate, "MM/dd/yyyy HH:mm");
    };

    const calculateProgress = (won: number, total: number) => {
        if (total === 0) return 0;
        return won / total;
    };

    // Belt data processing
    const beltData = (eventDetails?.stakes ?? [])
        .filter((stake: any) => stake.quantity > 0)
        .map((stake: any) => {
            let beltImage;
            switch (stake.type) {
                case "Token":
                case "Coin":
                    beltImage = AppImages.icCoinBelt;
                    break;
                case "Bronze":
                    beltImage = AppImages.icBronzeBelt;
                    break;
                case "Silver":
                    beltImage = AppImages.icSilverBelt;
                    break;
                case "Gold":
                    beltImage = AppImages.icGoldBelt;
                    break;
                case "Platinum":
                    beltImage = AppImages.icPlatinumBelt;
                    break;
                default:
                    beltImage = AppImages.icCoinBelt;
            }
            return { beltImage, amount: stake.quantity };
        });

    if (showCreateLoader) {
        return (
            <Modal transparent visible={showModal} animationType="slide">
                <View style={styles.overlay}>
                    <LinearGradient
                        colors={["#111827", "#000"]}
                        start={{ x: 0, y: 0 }}
                        end={{ x: 1, y: 1 }}
                        style={[styles.container, {
                            shadowColor: "rgba(232, 232, 232, 0.15)",
                            shadowOffset: { width: 0, height: -3 },
                            shadowOpacity: 1,
                            shadowRadius: 14,
                        }]}
                    >
                        <View style={styles.handleBar} />
                        <View style={styles.loadingContainer}>
                            <Text style={styles.loadingText}>Loading...</Text>
                        </View>
                    </LinearGradient>
                </View>
            </Modal>
        );
    }

    return (
        <Modal transparent visible={showModal} animationType="slide">
            <View style={styles.overlay}>
                <LinearGradient
                    colors={["#111827", "#000"]}
                    start={{ x: 0, y: 0 }}
                    end={{ x: 1, y: 1 }}
                    style={[styles.container, {
                        shadowColor: "rgba(232, 232, 232, 0.15)",
                        shadowOffset: { width: 0, height: -3 },
                        shadowOpacity: 1,
                        shadowRadius: 14,
                    }]}
                >
                    {/* Handle Bar */}
                    <View style={styles.handleBar} />

                    {/* Header */}
                    <View style={styles.header}>
                        <View style={{ flexDirection: "row", alignItems: "center", gap: 10 }}>
                            <Image
                                source={AppImages.icNotificationLogo}
                                style={{ height: 50, width: 70 }}
                                resizeMode="contain"
                            />
                            <Image
                                source={AppImages.icNotificationBarcode}
                                style={{ height: 25, width: 150 }}
                                resizeMode="contain"
                            />
                        </View>
                    </View>

                    {/* Scrollable Content */}
                    <ScrollView
                        ref={scrollViewRef}
                        style={styles.scrollView}
                        showsVerticalScrollIndicator={false}
                        contentContainerStyle={styles.scrollContent}
                    >
                        {/* Section 1: Player Card */}
                        <LinearGradient
                            colors={["rgba(31, 41, 55, 0.50)", "rgba(17, 24, 39, 0.50)"]}
                            start={{ x: 0, y: 0 }}
                            end={{ x: 1, y: 1 }}
                            style={styles.playerCard}
                        >
                            {/* Player Avatar with Badge */}
                            <View style={styles.avatarContainer}>
                                <Text style={styles.kallerLabel}>Kaller</Text>
                                <View style={styles.avatarWrapper}>
                                    <Avatar
                                        src={eventDetails?.organiserProfilePicture}
                                        variant="notificationLargePlayer"
                                    />
                                </View>
                                <View style={styles.statsContainer}>
                                    <View style={styles.statRow}>
                                        <Text style={styles.statLabel}>Games Won</Text>
                                        <Text style={styles.statValue}>
                                            {eventDetails?.participantProfiles?.[0]?.gamesWonCount || 0}
                                        </Text>
                                    </View>
                                    <ProgressBar
                                        progress={calculateProgress(
                                            eventDetails?.participantProfiles?.[0]?.gamesWonCount || 0,
                                            eventDetails?.participantProfiles?.[0]?.playedGamesCount || 1
                                        )}
                                        color={AppColors.green_44aeb4}
                                        style={styles.progressBar}
                                    />
                                    <View style={[styles.statRow, { marginTop: 2 }]}>
                                        <Text style={styles.statLabel}>Played Games</Text>
                                        <Text style={styles.statValue}>
                                            {eventDetails?.participantProfiles?.[0]?.playedGamesCount || 0}
                                        </Text>
                                    </View>
                                    <ProgressBar
                                        progress={1}
                                        color={AppColors.green_44aeb4}
                                        style={styles.progressBar}
                                    />
                                </View>
                            </View>

                            {/* Challenge Text */}
                            <View style={styles.challengeTextContainer}>
                                <Text style={styles.challengeText}>
                                    <Text style={{ fontWeight: "800" }}>{eventDetails?.organiserId}</Text>
                                    {" has challenged you in\n"}
                                    <Text style={{ fontWeight: "800" }}>{eventDetails?.gameTitle}</Text>
                                </Text>
                                <Text style={styles.willStartText}>Will start on:</Text>
                                <View style={styles.timeBox}>
                                    <Text style={styles.timeText}>
                                        {formatDate(eventDetails?.eventStartTime)}
                                    </Text>
                                </View>
                            </View>

                            {/* Game Image */}
                            <ImageComponent
                                url={eventDetails?.gameImage}
                                style={styles.gameImage}
                            />
                        </LinearGradient>

                        {/* Section 2: Challenge Reward (always visible) */}
                        <LinearGradient
                            colors={["rgba(31, 41, 55, 0.50)", "rgba(17, 24, 39, 0.50)"]}
                            start={{ x: 0, y: 0 }}
                            end={{ x: 1, y: 1 }}
                            style={styles.beltsCard}
                        >
                            <Text style={styles.beltsTitle}>Challenge Reward</Text>
                            <Text style={styles.beltsDescription}>
                                Both players put Belts into the pot. Winner takes it all. To accept
                                this challenge, you'll need to match {eventDetails?.organiserId}'s Belts
                            </Text>

                            <View style={styles.beltsRow}>
                                {beltData.map((belt: any, index: number) => (
                                    <View key={index} style={styles.beltItem}>
                                        <Image source={belt.beltImage} style={styles.beltImage} resizeMode="contain" />
                                        <View style={styles.beltAmountBox}>
                                            <Text style={styles.beltAmount}>{belt.amount}</Text>
                                        </View>
                                    </View>
                                ))}
                            </View>

                            <View style={styles.creditsRow}>
                                <View>
                                    <Text style={styles.creditsLabel}>
                                        Accept challenge to earn: <Text style={{ fontWeight: "700" }}>{eventDetails?.totalCredits || 350}</Text>
                                    </Text>
                                    <Text style={styles.creditsWorthLabel}>Credits worth</Text>
                                </View>
                                <View style={styles.priceBox}>
                                    <Text style={styles.priceText}>${eventDetails?.creditsWorth || 13}</Text>
                                </View>
                            </View>
                        </LinearGradient>

                        {/* Section 3: Action Buttons (always visible) */}
                        <View style={styles.actionButtonsRow}>
                            <TouchableOpacity
                                style={styles.negotiateButton}
                                onPress={() => handleActionPress("negotiate")}
                                disabled={isNegotiating}
                            >
                                <ImageComponent
                                    url={AppImages.icNotificationMessage}
                                    style={{ width: 16, height: 16 }}
                                />
                                <Text style={styles.negotiateButtonText}>Negotiate Terms</Text>
                            </TouchableOpacity>

                            <TouchableOpacity
                                style={styles.viewDetailsButton}
                                onPress={() => {
                                    setShowDetails(!showDetails);
                                    if (!showDetails) {
                                        // Scroll to bottom after a short delay to allow content to render
                                        setTimeout(() => {
                                            scrollViewRef.current?.scrollToEnd({ animated: true });
                                        }, 100);
                                    }
                                }}
                            >
                                <Text style={styles.viewDetailsText}>
                                    {showDetails ? "Hide details" : "View challenge details"}
                                </Text>
                            </TouchableOpacity>
                        </View>

                        {/* Section 4: Description (shown when View Details clicked) */}
                        {showDetails && eventDetails?.description && (
                            <LinearGradient
                                colors={["rgba(31, 41, 55, 0.50)", "rgba(17, 24, 39, 0.50)"]}
                                start={{ x: 0, y: 0 }}
                                end={{ x: 1, y: 1 }}
                                style={styles.rulesCard}
                            >
                                <Text style={styles.rulesTitle}>Rules & Description:</Text>
                                <Text style={styles.rulesText}>{eventDetails?.description}</Text>
                            </LinearGradient>
                        )}

                        {/* Section 5: Event Details Row (shown when View Details clicked) */}
                        {showDetails && (
                            <LinearGradient
                                colors={["rgba(31, 41, 55, 0.50)", "rgba(17, 24, 39, 0.50)"]}
                                start={{ x: 0, y: 0 }}
                                end={{ x: 1, y: 1 }}
                                style={styles.eventDetailsCard}
                            >
                                <View style={styles.eventDetailsRow}>
                                    <View style={styles.eventDetailItem}>
                                        <Text style={styles.eventDetailLabel}>Event name:</Text>
                                        <LinearGradient
                                            colors={["#1F2937", "#111827"]}
                                            start={{ x: 0, y: 0 }}
                                            end={{ x: 0, y: 1 }}
                                            style={styles.eventDetailValueBox}
                                        >
                                            <Text style={styles.eventDetailValue}>{eventDetails?.eventTitle || "N/A"}</Text>
                                        </LinearGradient>
                                    </View>
                                    <View style={styles.eventDetailItem}>
                                        <Text style={styles.eventDetailLabel}>Event Type</Text>
                                        <LinearGradient
                                            colors={["#1F2937", "#111827"]}
                                            start={{ x: 0, y: 0 }}
                                            end={{ x: 0, y: 1 }}
                                            style={styles.eventDetailValueBox}
                                        >
                                            <Text style={styles.eventDetailValue}>{eventDetails?.eventType || "Kall-Out"}</Text>
                                        </LinearGradient>
                                    </View>
                                    <View style={styles.eventDetailItem}>
                                        <Text style={styles.eventDetailLabel}>Play Type</Text>
                                        <LinearGradient
                                            colors={["#1F2937", "#111827"]}
                                            start={{ x: 0, y: 0 }}
                                            end={{ x: 0, y: 1 }}
                                            style={styles.eventDetailValueBox}
                                        >
                                            <Text style={styles.eventDetailValue}>{eventDetails?.playType || "1 VS 1"}</Text>
                                        </LinearGradient>
                                    </View>
                                    <View style={styles.eventDetailItem}>
                                        <Text style={styles.eventDetailLabel}>Game</Text>
                                        <LinearGradient
                                            colors={["#1F2937", "#111827"]}
                                            start={{ x: 0, y: 0 }}
                                            end={{ x: 0, y: 1 }}
                                            style={styles.eventDetailValueBox}
                                        >
                                            <Text style={styles.eventDetailValue}>{eventDetails?.gameTitle || "N/A"}</Text>
                                        </LinearGradient>
                                    </View>
                                    <View style={styles.eventDetailItem}>
                                        <Text style={styles.eventDetailLabel}>Device</Text>
                                        <LinearGradient
                                            colors={["#1F2937", "#111827"]}
                                            start={{ x: 0, y: 0 }}
                                            end={{ x: 0, y: 1 }}
                                            style={styles.eventDetailValueBox}
                                        >
                                            <Text style={styles.eventDetailValue}>{eventDetails?.device || "PC"}</Text>
                                        </LinearGradient>
                                    </View>
                                    <View style={styles.eventDetailItem}>
                                        <Text style={styles.eventDetailLabel}>Gamer Id</Text>
                                        <LinearGradient
                                            colors={["#1F2937", "#111827"]}
                                            start={{ x: 0, y: 0 }}
                                            end={{ x: 0, y: 1 }}
                                            style={styles.eventDetailValueBox}
                                        >
                                            <Text style={styles.eventDetailValue}>{eventDetails?.gamerID?.organiser || eventDetails?.organiserId || "N/A"}</Text>
                                        </LinearGradient>
                                    </View>
                                </View>
                            </LinearGradient>
                        )}
                    </ScrollView>

                    {/* Fixed Footer - Accept/Reject Buttons */}
                    <View style={styles.footer}>
                        <TouchableOpacity
                            style={styles.rejectButton}
                            onPress={() => handleActionPress("reject")}
                        >
                            <Text style={styles.rejectButtonText}>Reject</Text>
                        </TouchableOpacity>

                        <TouchableOpacity
                            style={styles.acceptButton}
                            onPress={() => handleActionPress("accept")}
                        >
                            <Text style={styles.acceptButtonText}>Accept</Text>
                        </TouchableOpacity>
                    </View>
                </LinearGradient>
            </View>
        </Modal>
    );
}

const styles = StyleSheet.create({
    overlay: {
        flex: 1,
        justifyContent: "flex-end",
        backgroundColor: "rgba(0, 0, 0, 0.5)",
    },
    container: {
        borderTopLeftRadius: 16,
        borderTopRightRadius: 16,
        borderWidth: 1,
        borderBottomWidth: 0,
        borderColor: "#DC2626",
        paddingHorizontal: 12,
        paddingTop: 8,
        paddingBottom: 20,
        height: "80%",
    },
    handleBar: {
        width: 40,
        height: 4,
        backgroundColor: "#4B5563",
        borderRadius: 2,
        alignSelf: "center",
        marginBottom: 12,
    },
    header: {
        marginBottom: 12,
    },
    scrollView: {
        flex: 1,
    },
    scrollContent: {
        gap: 12,
        paddingBottom: 12,
    },
    loadingContainer: {
        flex: 1,
        justifyContent: "center",
        alignItems: "center",
    },
    loadingText: {
        color: "#FFF",
        fontSize: 16,
    },

    // Player Card
    playerCard: {
        flexDirection: "row",
        height: 115,
        padding: 6,
        justifyContent: "space-between",
        alignItems: "center",
        borderRadius: 8,
        borderWidth: 0.8,
        borderColor: "#374151",
    },
    avatarContainer: {
        width: 90,
        alignItems: "center",
    },
    kallerLabel: {
        color: "#DC2626",
        fontSize: 10,
        fontWeight: "600",
        marginBottom: 2,
    },
    avatarWrapper: {
        backgroundColor: "#604e74",
        borderRadius: 50,
        overflow: "hidden",
    },
    statsContainer: {
        marginTop: 4,
    },
    statRow: {
        flexDirection: "row",
        justifyContent: "space-between",
    },
    statLabel: {
        color: "#FFF",
        fontSize: 8,
    },
    statValue: {
        color: "#FFF",
        fontSize: 8,
        fontWeight: "700",
    },
    progressBar: {
        marginTop: 2,
        height: 4,
        borderRadius: 2,
        backgroundColor: AppColors.black,
    },
    challengeTextContainer: {
        flex: 1,
        alignItems: "center",
        paddingHorizontal: 8,
    },
    challengeText: {
        color: "#FFF",
        textAlign: "center",
        fontSize: 11.67,
        fontWeight: "800",
        lineHeight: 13.5,
    },
    willStartText: {
        color: "#FFF",
        fontSize: 13.5,
        fontWeight: "700",
        marginTop: 8,
    },
    timeBox: {
        padding: 4,
        paddingHorizontal: 6,
        borderRadius: 4,
        backgroundColor: "#A3881D",
        marginTop: 4,
    },
    timeText: {
        color: "#FFF",
        fontSize: 11,
        fontWeight: "700",
    },
    gameImage: {
        width: 69,
        height: 75,
        borderRadius: 8,
    },

    // Rules Card
    rulesCard: {
        padding: 12,
        borderRadius: 8,
        borderWidth: 0.8,
        borderColor: "#374151",
    },
    rulesTitle: {
        color: "#FFF",
        fontSize: 16,
        fontWeight: "600",
        marginBottom: 8,
    },
    rulesText: {
        color: "#FFF",
        fontSize: 12,
        lineHeight: 16,
    },

    // Belts Card
    beltsCard: {
        padding: 16,
        borderRadius: 8,
        borderWidth: 0.8,
        borderColor: "#374151",
        alignItems: "center",
    },
    beltsTitle: {
        color: "#FFF",
        fontSize: 14,
        fontWeight: "600",
        marginBottom: 6,
    },
    beltsDescription: {
        color: "#FFF",
        fontSize: 11,
        textAlign: "center",
        marginBottom: 12,
    },
    beltsRow: {
        flexDirection: "row",
        justifyContent: "center",
        gap: 12,
        marginBottom: 12,
    },
    beltItem: {
        alignItems: "center",
    },
    beltImage: {
        width: 50,
        height: 50,
    },
    beltAmountBox: {
        backgroundColor: "#000",
        borderRadius: 4,
        paddingHorizontal: 12,
        paddingVertical: 4,
        marginTop: 4,
    },
    beltAmount: {
        color: "#FFF",
        fontSize: 12,
        fontWeight: "700",
    },
    creditsRow: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        width: "100%",
    },
    creditsLabel: {
        color: "#FFF",
        fontSize: 12,
    },
    creditsWorthLabel: {
        color: "#FFF",
        fontSize: 12,
        fontWeight: "700",
    },
    priceBox: {
        backgroundColor: "#374151",
        borderRadius: 8,
        paddingVertical: 8,
        paddingHorizontal: 16,
    },
    priceText: {
        color: "#FFF",
        fontSize: 18,
        fontWeight: "700",
    },

    // Action Buttons
    actionButtonsRow: {
        flexDirection: "row",
        justifyContent: "space-between",
        gap: 12,
    },
    negotiateButton: {
        flexDirection: "row",
        alignItems: "center",
        gap: 6,
        paddingVertical: 8,
        paddingHorizontal: 10,
        borderRadius: 4,
        borderWidth: 1,
        borderColor: "#FFF",
    },
    negotiateButtonText: {
        color: "#FFF",
        fontSize: 11,
        fontWeight: "500",
    },
    viewDetailsButton: {
        paddingVertical: 10,
        paddingHorizontal: 14,
        borderRadius: 6,
        borderWidth: 1,
        borderColor: "#DC2626",
    },
    viewDetailsText: {
        color: "#DC2626",
        fontSize: 12,
        fontWeight: "600",
    },

    // Footer
    footer: {
        flexDirection: "row",
        gap: 12,
        marginTop: 12,
    },
    rejectButton: {
        flex: 0.8,
        paddingVertical: 12,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: "#DC2626",
        alignItems: "center",
        justifyContent: "center",
    },
    rejectButtonText: {
        color: "#DC2626",
        fontSize: 14,
        fontWeight: "600",
    },
    acceptButton: {
        flex: 1.2,
        paddingVertical: 12,
        borderRadius: 8,
        backgroundColor: AppColors.appGreenColor_51AF07,
        alignItems: "center",
        justifyContent: "center",
    },
    acceptButtonText: {
        color: "#FFF",
        fontSize: 14,
        fontWeight: "600",
    },

    // Event Details Card
    eventDetailsCard: {
        borderRadius: 8,
        borderWidth: 0.8,
        borderColor: "#374151",
        padding: 10,
    },
    eventDetailsRow: {
        flexDirection: "row",
        flexWrap: "wrap",
        gap: 8,
    },
    eventDetailItem: {
        minWidth: "30%",
        alignItems: "flex-start",
    },
    eventDetailLabel: {
        color: "#9CA3AF",
        fontSize: 10,
        marginBottom: 4,
    },
    eventDetailValueBox: {
        borderRadius: 8,
        borderWidth: 0.8,
        borderColor: "#374151",
        paddingHorizontal: 10,
        paddingVertical: 4,
    },
    eventDetailValue: {
        color: "#FFF",
        fontSize: 11,
        fontWeight: "600",
    },
});

export default MobileEventSharePopup;
